import { Component, OnInit } from '@angular/core';
import { ToastService } from './toast/toast.service';
import { CurrentAccount } from "./interfaces/current-account.interface";
import { CurrentUser } from "./interfaces/current-user.interface";
import { CurrentAccountService } from "./services/current-account.service";
import { CurrentUserService } from "./services/current-user.service";
import { LanguageService } from "./services/language.service";
import { switchMap } from "rxjs";
import { tap } from "rxjs/operators";
import { BrandingService } from "./services/branding.service";
import { Branding, ColorThemeFieldsFragment } from './graphql/gql';
import tinycolor from 'tinycolor2';
import { SHADE_KEYS, shadeKey } from './BrandablePreset';
import { appConstants } from './app-constants';
import { SessionDataService } from './services/session-data.service';

type BrandingMapEntry = {
  localVariable: string,
  legacyEntry: keyof ColorThemeFieldsFragment
}

const LEGACY_BRANDING_MAP: BrandingMapEntry[] = [
  {localVariable: '--b-ci-fill', legacyEntry: 'pageTitle'},
  {localVariable: '--b-ci-content-text', legacyEntry: 'navigationMainMenuHeader'},
  {localVariable: '--b-ci-text', legacyEntry: 'navigationMainMenuTitle'},
  {localVariable: '--b-ci-accent', legacyEntry: 'accent'},
  {localVariable: '--b-background', legacyEntry: 'bodyBackground'}
]

/**
 * The AppComponent class represents the root component of the application.
 *
 * The component is responsible for initializing the application, subscribing to changes in the user and account data,
 * and applying the appropriate branding based on the current user or the hosting environment.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit {
  currentUser: CurrentUser | null | undefined = null;
  currentAccount: CurrentAccount | null | undefined = null;
  hasLoadedBranding = false;
  sessionData = this.sessionDataService.sessionData;

  constructor(
    private brandingService: BrandingService,
    private currentUserService: CurrentUserService,
    private currentAccountService: CurrentAccountService,
    private languageService: LanguageService,
    private sessionDataService: SessionDataService,
    private toastService: ToastService) {}

  /**
   * Performs initialization tasks when the component is initialized.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.updateFaviconElement();

    // Initialize language switching based on user preference
    this.languageService.initializeLanguageSwitching();

    this.currentUserService.currentUser.pipe(
      tap(result => this.currentUser = result?.currentUser as CurrentUser),
      switchMap(() => {
        return this.currentAccountService.currentAccount;
      })
    ).subscribe(accountData => {
      this.currentAccount = accountData?.currentAccount;
      if (this.currentAccount?.branding) {
        this.applyBranding(this.currentAccount.branding as Branding);
      } else {
        this.brandingService.getBrandingByHost(window.location.host).subscribe(
          ({ brandingByHost }) => { this.applyBranding(brandingByHost as Branding); }
        );
      }
    });

    this.toastService.initLegacyFlashMessageHandling()
  }

  private updateFaviconElement() {
    const linkElement = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (linkElement) {
      linkElement.href = appConstants.FAVICON_URL;
    }
  }

  private monochromeScale(base: tinycolor.Instance, semantic: string): [string, string][] {
    const hsl = tinycolor(base).toHsl();
    const result: [string, string][] = [];

    for (let i = 0; i < SHADE_KEYS.length; ++i) {
      hsl.l = 1 - ((i+0.5)/SHADE_KEYS.length);
      result.push([shadeKey(semantic, SHADE_KEYS[i]), tinycolor(hsl).toHex8String()])
    }

    return result;
  }

  applyBranding(branding: Branding): void {
    if (!branding) {
      return;
    }

    if (branding?.colorTheme) {
      this.applyTheme(branding.colorTheme as ColorThemeFieldsFragment);
    }

    if (branding?.pageTitle) {
      document.title = branding.pageTitle;
    }

    this.hasLoadedBranding = true;
  }

  /**
   * Applies the given theme to the root element of the document.
   *
   * @param {object} theme - The theme object containing key-value pairs for the theme properties.
   * @returns {void}
   */
  applyTheme(theme: ColorThemeFieldsFragment): void {
    if (!theme) {
      return;
    }

    const rootElement = document.querySelector(':root') as HTMLElement;

    LEGACY_BRANDING_MAP.forEach(map => {
      rootElement.style.setProperty(map.localVariable, theme[map.legacyEntry] || null)
    })

    this.monochromeScale(tinycolor(rootElement.style.getPropertyValue('--b-ci-fill')), 'surface').forEach(tuple => {
      rootElement.style.setProperty(tuple[0], tuple[1]);
    })

    this.monochromeScale(tinycolor(rootElement.style.getPropertyValue('--b-ci-accent')), 'primary').forEach(tuple => {
      rootElement.style.setProperty(tuple[0], tuple[1]);
    })
  }
}
